
import React, { useState, useEffect } from 'react';
import { mockActivities, mockDeals, mockTasks, mockEmployees } from '../mockData';
import ContactList from '../components/ContactList';
import ContactDetail from '../components/ContactDetail';
import AddContactModal from '../components/AddContactModal';
import { ConfirmationDialog } from '../components/ConfirmationDialog';
import { Contact, Activity, Task, Country, User } from '../types';
import { apiService } from '../services/apiService';

interface CRMModuleProps {
  currentUser: User;
  activeCountry: Country;
}

const CRMModule: React.FC<CRMModuleProps> = ({ currentUser, activeCountry }) => {
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [selectedContact, setSelectedContact] = useState<Contact | null>(null);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [activities, setActivities] = useState<Activity[]>(mockActivities);
  const [tasks, setTasks] = useState<Task[]>(mockTasks);
  const [isLoading, setIsLoading] = useState(true);
  
  // Deletion State
  const [contactToDelete, setContactToDelete] = useState<string | null>(null);

  const isAdmin = currentUser.role === 'Admin';
  const canAddEdit = isAdmin || currentUser.role === 'Add/Edit';
  const canDelete = isAdmin;
  const isReadOnly = currentUser.role === 'Read Only';

  useEffect(() => {
    loadContacts();
  }, []);

  const loadContacts = async () => {
    setIsLoading(true);
    const data = await apiService.getContacts();
    setContacts(data);
    setIsLoading(false);
  };

  const handleSelectContact = (contact: Contact) => {
    setSelectedContact(contact);
  };

  const handleUpdateContact = async (updated: Contact) => {
    if (isReadOnly) return;
    await apiService.saveContact(updated);
    setContacts(prev => prev.map(c => c.id === updated.id ? updated : c));
    setSelectedContact(updated);
  };

  const handleAddContact = async (newContact: Contact) => {
    if (isReadOnly) return;
    await apiService.saveContact(newContact);
    setContacts(prev => [newContact, ...prev]);
  };

  const handleDeleteContact = async () => {
    if (!contactToDelete || !canDelete) return;
    
    try {
      const success = await apiService.deleteContact(contactToDelete);
      if (success) {
        setContacts(prev => prev.filter(c => c.id !== contactToDelete));
        setSelectedContact(null);
      }
    } catch (e) {
      console.error("Deletion failed", e);
      alert("System could not revoke customer record. Verify core connectivity.");
    } finally {
      setContactToDelete(null);
    }
  };

  const handleAddActivity = (act: Activity) => {
    if (isReadOnly) return;
    setActivities(prev => [act, ...prev]);
  };

  const handleAddTask = (t: Task) => {
    if (isReadOnly) return;
    setTasks(prev => [t, ...prev]);
  };

  const handleToggleTask = (id: string) => {
    if (isReadOnly) return;
    setTasks(prev => prev.map(t => t.id === id ? { ...t, status: t.status === 'Completed' ? 'Pending' : 'Completed' } : t));
  };

  if (isLoading) {
    return (
      <div className="h-full flex items-center justify-center p-12">
        <div className="flex flex-col items-center gap-4">
          <div className="w-10 h-10 border-4 border-indigo-100 border-t-indigo-600 rounded-full animate-spin"></div>
          <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">Loading CRM Node...</p>
        </div>
      </div>
    );
  }

  if (selectedContact) {
    return (
      <>
        <ContactDetail 
          contact={selectedContact}
          currentUser={currentUser}
          deals={mockDeals.filter(d => d.contactId === selectedContact.id)}
          activities={activities.filter(a => a.contactId === selectedContact.id)}
          tasks={tasks.filter(t => t.contactId === selectedContact.id)}
          employees={mockEmployees}
          onBack={() => setSelectedContact(null)}
          onUpdateContact={handleUpdateContact}
          onDeleteContact={canDelete ? (id) => setContactToDelete(id) : undefined}
          onAddActivity={handleAddActivity}
          onAddTask={handleAddTask}
          onToggleTask={handleToggleTask}
        />
        
        <ConfirmationDialog 
          isOpen={!!contactToDelete}
          title="Revoke Customer Registry"
          message={`Are you sure you want to permanently delete "${selectedContact.companyName}" from the CRM? This action is audited and cannot be undone.`}
          confirmText="Delete Record"
          cancelText="Discard"
          isDestructive={true}
          onConfirm={handleDeleteContact}
          onCancel={() => setContactToDelete(null)}
        />
      </>
    );
  }

  return (
    <div className="animate-in fade-in duration-500">
      <div className="px-8 pt-6">
        <span className="text-[10px] font-black bg-indigo-50 text-indigo-600 px-3 py-1 rounded-full border border-indigo-100 uppercase tracking-widest">
            {activeCountry} CRM DATABASE
        </span>
      </div>
      <ContactList 
        contacts={contacts} 
        currentUser={currentUser}
        onSelectContact={handleSelectContact} 
        onAddContact={canAddEdit ? () => setIsAddModalOpen(true) : () => {}} 
      />
      {canAddEdit && (
        <AddContactModal 
            isOpen={isAddModalOpen} 
            onClose={() => setIsAddModalOpen(false)} 
            onSave={handleAddContact} 
        />
      )}
    </div>
  );
};

export default CRMModule;
